import type { UserSessionV2Output } from '@allocab/allocab-auth-service-types';
import { LoginOutput } from '@allocab/allocab-commons-shared-types';
import { QueryClient } from '@tanstack/react-query';
import { jwtDecode } from 'jwt-decode';

import type { UserInfo } from '../../../web/services/analytics/types';
import { getConfig } from '../../config';
import { getRandomNumber, noop } from '../../utils/common';
import { queryClient, queryKeys } from '../datastore';
import { storage } from '../storage';
import { ToastManager } from '../toast';
import { t } from '../translations/translation';

export const updateTokens = (queryClient: QueryClient, newTokens: LoginOutput) => {
  queryClient.cancelQueries({ queryKey: queryKeys.tokens() });
  queryClient.setQueryData(queryKeys.tokens(), newTokens);
};

export const clearTokens = async (queryClient: QueryClient) => {
  storage.removeItem('tokens');
  storage.removeItem('operatorId');
  queryClient.setQueryData(queryKeys.tokens(), null);
  queryClient.removeQueries({ queryKey: queryKeys.passenger.root() });
};

const handleDriverAppInstall = () => {
  getConfig().openWebUrl?.('http://dl.allocab.com/driver-next/index.html');
};

export interface SigninOptions {
  allowedSessionTypes?: UserSessionV2Output['type'][];
  onRejectedType?: (session: UserSessionV2Output) => void;
}

const getSigninRejection = (session: UserSessionV2Output, options?: SigninOptions) => {
  const allowedTypes = options?.allowedSessionTypes ?? ['Passenger'];
  if (!allowedTypes.includes(session.type)) return 'type';
  if (session.accountCategory === 'HOTELS') return 'hotels';
  return null;
};

/**
 * Vrai si `handleSuccessfulSignin` ouvre la session pour ces tokens. Un compte d'un autre type ou
 * un compte hôtel obtient bien des tokens, mais reste sur l'écran de connexion : à vérifier avant
 * de compter une connexion.
 */
export const isSigninAccepted = (tokens: LoginOutput, options?: SigninOptions) =>
  getSigninRejection(jwtDecode(tokens.accessToken) as UserSessionV2Output, options) === null;

export const handleSuccessfulSignin = (tokens: LoginOutput, options?: SigninOptions) => {
  const session = jwtDecode(tokens.accessToken) as UserSessionV2Output;
  const rejection = getSigninRejection(session, options);

  if (rejection === 'type') {
    if (options?.onRejectedType) {
      options.onRejectedType(session);
    } else {
      ToastManager.show({
        message: t('pwa.auth-picker-screen.warn-passenger'),
        severity: 'warning',
        timer: 0,
        action: { label: t('pwa.common.install'), onPress: handleDriverAppInstall },
      });
    }
    return;
  }

  if (rejection === 'hotels') {
    ToastManager.show({
      message: t('pwa.auth-picker-screen.warn-hotels'),
      severity: 'warning',
      timer: 0,
    });
    return;
  }

  try {
    getConfig().telemetry.setUser(
      session.userKey,
      session.accountCategory ? { accountCategory: session.accountCategory } : undefined,
    );
  } catch {
    // Amplitude may not be initialized (e.g. driver-web)
  }

  updateTokens(queryClient, tokens);
};

export const generateRandomPassword = () => {
  const positions = Array.from({ length: 16 }).map(_ => 'a');
  positions[0] = 'l';
  positions[1] = 'u';
  positions[2] = 'n';

  // Shuffle array
  for (let i = positions.length - 1; i > 0; i--) {
    const j = getRandomNumber(0, i);
    [positions[i], positions[j]] = [positions[j], positions[i]];
  }

  const lowercaseCharSet = 'abcdefghijklmnopqrstuvwxyz';
  const uppercaseCharSet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const numberCharSet = '0123456789';
  const allCharSet = `${lowercaseCharSet}${uppercaseCharSet}${numberCharSet}`;

  return positions
    .map(type => {
      let charSet = allCharSet;

      if (type === 'l') {
        charSet = lowercaseCharSet;
      } else if (type === 'u') {
        charSet = uppercaseCharSet;
      } else if (type === 'n') {
        charSet = numberCharSet;
      }

      return charSet.charAt(getRandomNumber(0, charSet.length));
    })
    .join('');
};

export const getUserInfoFromSession = (): UserInfo | null => {
  try {
    const tokens = storage.getJSONItem<LoginOutput>('tokens');

    if (!tokens) return null;

    const session = jwtDecode(tokens.accessToken) as UserSessionV2Output;
    const accountCategory = session.accountCategory;
    const [accountId, userId] = (session.userKey || '').match(/\d+/g) || [];

    return {
      userId: accountId && userId ? `${accountId}-${userId}` : undefined,
      userAccountId: accountId || undefined,
      accountType: !accountCategory
        ? undefined
        : accountCategory === 'B2C'
          ? 'b2c'
          : accountCategory === 'SMB'
            ? 'smb'
            : accountCategory === 'HOTELS'
              ? 'hotels'
              : accountCategory === 'OTHER_B2B'
                ? 'other_b2b'
                : 'key_account',
    };
  } catch (error) {
    noop(error);
    return null;
  }
};
